import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Link, NavLink as NavLinkRRD, useLocation, useHistory } from 'react-router-dom'
import { Button, Collapse, Nav, Navbar, NavbarBrand, NavItem, NavLink } from 'reactstrap'
import AdminsApiV2 from 'api/v2/admins-api-v2.js'

const propTypes = {
  /** function used to make sidenav mini or normal */
  toggleSidebar : PropTypes.func,
  /** prop to know if the sidenav is mini or normal */
  isSidebarOpen : PropTypes.bool,
  /** links that will be displayed inside the component */
  routes        : PropTypes.arrayOf(PropTypes.object),
  /** Logo */
  logo          : PropTypes.shape({
    /** Logo name */
    name       : PropTypes.string,
    sup        : PropTypes.string,
    /** innerLink is for links that will direct the user within the app
      * it will be rendered as <Link to='...'>...</Link> tag
    */
    innerLink  : PropTypes.string,
    /** outterLink is for links that will direct the user outside the app
     *  it will be rendered as simple <a href='...'>...</a> tag
    */
    outterLink : PropTypes.string,
    /** the image src of the logo */
    imgSrc     : PropTypes.string.isRequired,
    /** the alt for the img */
    imgAlt     : PropTypes.string.isRequired
  })
}

const Sidebar = ({ logo, routes, isSidebarOpen, toggleSidebar }) => {
  const [collapseStates, setCollapseStates] = useState({})
  const [hoverItem, setHoverItem] = useState(null) // gunakan path unik, bukan index
  const history = useHistory()
  const location = useLocation()

  // === Helpers: active route detection ===
  const isRouteActive = (route) => {
    if (!route) return false

    // kalau parent collapse, cek children
    if (route.collapse && route.views) {
      return route.views.some((child) => isRouteActive(child))
    }

    if (!route.layout || !route.path) return false
    const fullPath = `${route.layout}${route.path}`

    // exact atau prefix dengan slash (untuk /detail/123)
    return (
      location.pathname === fullPath ||
      location.pathname.startsWith(`${fullPath}/`)
    )
  }

  const getCollapseInitialState = (routesArr) => {
    if (!routesArr) return false
    for (let i = 0; i < routesArr.length; i++) {
      const r = routesArr[i]
      if (isRouteActive(r)) return true
      if (r.collapse && r.views && getCollapseInitialState(r.views)) return true
    }
    return false
  }

  const getCollapseStates = (routesArr) => {
    let initialState = {}
    routesArr.forEach((route) => {
      if (route.collapse) {
        initialState = {
          ...initialState,
          [route.state_key]: getCollapseInitialState(route.views),
          ...getCollapseStates(route.views)
        }
      }
    })
    return initialState
  }

  useEffect(() => {
    // inisialisasi collapse berdasarkan URL saat ini
    setCollapseStates(getCollapseStates(routes || []))

    // **fix halaman ke-overlay abu-abu setelah login**
    // pastikan tidak ada overlay mobile yang nyangkut
    document.body.classList.remove('g-sidenav-show')

    return () => {
      document.body.classList.remove('g-sidenav-show')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // active class helper untuk NavItem leaf
  const activeRoute = (route) => (isRouteActive(route) ? 'active' : '')

  // autoclose di mobile
  const closeSidenav = () => {
    if (window.innerWidth < 1080) {
      toggleSidebar()
    }
  }

  // === Generate menu items ===
  const createLinks = (routesArr) =>
    routesArr.map((route, key) => {
      if (route.redirect || route.invisible) return null

      // ===== GROUP / PARENT (collapse) =====
      if (route.collapse) {
        const isOpen = !!collapseStates[route.state_key]
        const isParentActive = getCollapseInitialState(route.views)

        return (
          <NavItem key={route.state_key || route.name || key}>
            <NavLink
              data-toggle='collapse'
              aria-expanded={isOpen}
              className={classnames({
                active: isParentActive
              })}
              onClick={(e) => {
                e.preventDefault()
                // Accordion behaviour: satu group terbuka, yang lain tutup
                setCollapseStates((prev) => {
                  const closedAll = Object.keys(prev).reduce(
                    (acc, k) => ({ ...acc, [k]: false }),
                    {}
                  )
                  return {
                    ...closedAll,
                    [route.state_key]: !prev[route.state_key]
                  }
                })
              }}
            >
              {route.icon ? (
                <>
                  <i className={route.icon} />
                  <span className='nav-link-text'>{route.name}</span>
                </>
              ) : route.miniName ? (
                <>
                  <span className='sidenav-mini-icon'> {route.miniName} </span>
                  <span className='sidenav-normal'> {route.name} </span>
                </>
              ) : (
                <span>{route.name}</span>
              )}
            </NavLink>

            <Collapse isOpen={isOpen}>
              <Nav className='nav-sm flex-column'>
                {createLinks(route.views)}
              </Nav>
            </Collapse>
          </NavItem>
        )
      }

      // ===== LEAF / SUB-MENU =====
      const fullPath = `${route.layout}${route.path}`
      const isActive = isRouteActive(route)
      const isHovered = hoverItem === fullPath

      return (
        <NavItem
          className={activeRoute(route)}
          key={route.key || fullPath || key}
        >
          <NavLink
            to={fullPath}
            activeClassName=''
            onClick={closeSidenav}
            tag={NavLinkRRD}
            onMouseEnter={() => setHoverItem(fullPath)}
            onMouseLeave={() => setHoverItem(null)}
            style={{
              // hanya SUB-MENU yang diberi highlight
              backgroundColor: isActive || isHovered ? '#8d9e74' : 'transparent',
              color: isActive || isHovered ? '#fff' : '#000',
              borderRadius: '8px',
              padding: '8px 14px',
              transition: 'all 0.2s ease'
            }}
          >
            {route.icon !== undefined ? (
              <>
                <i className={route.icon} />
                <span className='nav-link-text'>{route.name}</span>
              </>
            ) : route.miniName !== undefined ? (
              <>
                <span className='sidenav-mini-icon'> {route.miniName} </span>
                <span className='sidenav-normal'> {route.name } </span>
              </>
            ) : (
              route.name
            )}
          </NavLink>
        </NavItem>
      )
    })

  const handleLogout = async () => {
    await AdminsApiV2.logout()
      .then(() => {
        history.go('/')
      })
      .catch((error) => {
        // user already logged out
        if (error.response && error.response.status === 401) {
          history.go('/')
        } else {
          alert(error.message)
        }
      })
  }

  const handleUpdatePassword = () => {
    history.push('/app/update-password')
  }

  const ScrollBarInner = () => (
    <div className='scrollbar-inner'>
      {/* Sidebar Header */}
      <div className='sidenav-header d-flex align-items-center'>
        <NavbarBrand
          tag={Link}
          name={logo.name}
          sup={logo.sup}
          to={'/'}
        >
          <img height={300} width={100} src={logo.imgSrc} alt={logo.imgAlt} />
          <span className='ml-3'>{logo.name}</span>
          {logo.sup && <sup className='font-weight-light'>{logo.sup}</sup>}
        </NavbarBrand>

        <div className='ml-auto'>
          <div
            className={classnames('sidenav-toggler d-none d-xl-block', {
              active: isSidebarOpen
            })}
            onClick={toggleSidebar}
          >
            <div className='sidenav-toggler-inner'>
              <i className='sidenav-toggler-line' />
              <i className='sidenav-toggler-line' />
              <i className='sidenav-toggler-line' />
            </div>
          </div>
        </div>
      </div>

      <div className='navbar-inner'>
        <Collapse navbar isOpen={true}>
          <Nav navbar>
            {createLinks(routes)}
          </Nav>
          <hr className='my-3' />
          <Nav className='mb-md-3' navbar>
            <NavItem onClick={handleUpdatePassword}>
              <NavLink>
                <Button outline block color='secondary' size='sm'>
                  <span className='nav-link-text'>
                    {'Update Password'}
                  </span>
                </Button>
              </NavLink>
            </NavItem>
            <NavItem onClick={handleLogout}>
              <NavLink>
                <Button outline block color='primary' size='sm'>
                  <i className='fas fa-sign-out-alt' />
                  <span className='nav-link-text'>
                    {'Logout'}
                  </span>
                </Button>
              </NavLink>
            </NavItem>
          </Nav>
        </Collapse>
      </div>
    </div>
  )

  const ScrollBarBody = () => {
    const isPerfectScrollbarSupported = typeof navigator !== 'undefined' &&
      navigator.platform &&
      navigator.platform.indexOf('Win') > -1

    if (isPerfectScrollbarSupported) {
      return (
        <PerfectScrollbar>
          <ScrollBarInner />
        </PerfectScrollbar>
      )
    }
    return <ScrollBarInner />
  }

  return (
    <Navbar
      className='sidenav navbar-vertical navbar-expand-xs navbar-light bg-white fixed-left'
      // NOTE: onMouseEnter / onMouseLeave DIHAPUS
      // supaya nggak lagi main-main dengan class body & overlay
    >
      <ScrollBarBody />
    </Navbar>
  )
}

Sidebar.defaultProps = {
  routes        : [ {} ],
  toggleSidebar : () => {},
  isSidebarOpen : false
}

Sidebar.propTypes = propTypes

export default Sidebar
